import React from "react";
import { cn } from "@/utils/cn";
import { IconCircleFilled } from "@tabler/icons-react";
import { TeeLink } from "./tee-link";

export type ValueType = "boolean" | "number" | "currency" | "text";
export type StatusIndicator = "active" | "inactive" | "none";

export interface TeeDataDisplayProps {
  /**
   * Label/Rule name
   */
  label: string;

  /**
   * Value type determines how the value is displayed
   */
  valueType?: ValueType;

  /**
   * The actual value to display
   */
  value: string | number | boolean;

  /**
   * Unit label (e.g., "Days", "Count", "Minutes", "Allow?", "Charge")
   */
  unit?: string;

  /**
   * Status indicator - 'active' (green), 'inactive' (red), 'none' (no icon)
   */
  status?: StatusIndicator;

  /**
   * Secondary label for price range display (e.g., "PRICE RANGE (MINIMUM - MAXIMUM)")
   */
  secondaryLabel?: string;

  /**
   * Secondary value for price range display (e.g., "$130 - $150.00")
   */
  secondaryValue?: string | number;

  /**
   * Whether this is the last item in a list (applies bottom border-radius)
   */
  isLast?: boolean;

  /**
   * Action label for the action button
   */
  actionLabel?: string;
  
  /**
   * Action icon for the action button
   */
  leadingActionIcon?: React.ReactNode;

  /**
   * On action.
   */
  onAction?: () => void;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export const TeeDataDisplay = React.forwardRef<
  HTMLDivElement,
  TeeDataDisplayProps
>(
  (
    {
      label,
      valueType = "text",
      value,
      unit,
      status = "active",
      secondaryLabel,
      secondaryValue,
      isLast = false,
      actionLabel,
      leadingActionIcon,
      onAction,
      className,
    },
    ref,
  ) => {
    const isInactive = status === "inactive";
    const showIcon = status !== "none";

    // Determine icon color based on status
    const iconColorClass = isInactive ? "text-[#923430]" : "text-[#15803d]";

    // Text color changes based on status
    const labelColorClass = isInactive
      ? "text-[var(--text-Label-disabled)]"
      : "text-[var(--text-input-field-filled)]";
    const unitColorClass = isInactive
      ? "text-[var(--text-Label-disabled)]"
      : "text-[var(--text-input-field-filled)]";

    // Format value based on type
    const formatValue = () => {
      if (valueType === "boolean") {
        return typeof value === "boolean"
          ? value
            ? "Yes"
            : "No"
          : String(value);
      }
      if (valueType === "currency") {
        return typeof value === "number"
          ? `$${value.toFixed(2)}`
          : String(value);
      }
      return String(value);
    };

    // If secondary values are provided, render as a three-column layout
    if (secondaryLabel && secondaryValue !== undefined) {
      return (
        <div
          ref={ref}
          className={cn(
            "flex items-center gap-0 border border-[var(--border-default)] bg-[var(--background-card)] rounded-b-[var(--spacing-border-radius-xs,6px)]",
            className,
          )}
        >
          {/* Left Column - Label and Value */}
          <div className="flex flex-1 items-center gap-3 px-4 py-3 rounded-bl-[var(--spacing-border-radius-xs,6px)]">
            {/* Status Indicator Icon - Only show if status is not 'none' */}
            {showIcon && (
              <IconCircleFilled
                size={10}
                className={cn("flex-shrink-0", iconColorClass)}
              />
            )}
            <p
              className={cn("flex-1 text-body-s font-normal", labelColorClass)}
            >
              {label}
            </p>
          </div>

          {/* Middle Column - Primary Value */}
          <div className="w-[140px] px-4 py-3 text-right">
            <p className={cn("text-body-s font-semibold", unitColorClass)}>
              {formatValue()}
            </p>
          </div>

          {/* Right Column - Secondary Value Only */}
          <div className="flex-1 px-4 py-3 rounded-br-[var(--spacing-border-radius-xs,6px)]">
            <p
              className={cn(
                "text-body-s font-semibold text-right",
                unitColorClass,
              )}
            >
              {secondaryValue}
            </p>
          </div>
        </div>
      );
    }

    // Standard single-row layout
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-3 border border-[var(--border-default)] bg-[var(--background-card)] px-4 py-3",
          !isLast && "border-b-0",
          isLast && "rounded-b-[var(--spacing-border-radius-xs,6px)]",
          className,
        )}
      >
        {/* Status Indicator Icon - Only show if status is not 'none' */}
        {showIcon && (
          <IconCircleFilled
            size={10}
            className={cn("flex-shrink-0", iconColorClass)}
          />
        )}

        {/* Label */}
        <p className={cn("flex-1 text-body-s font-normal overflow-hidden text-ellipsis", labelColorClass)}>
          {label}
        </p>

        {/* Value Section */}
        <div className="flex items-center gap-6 ml-auto">
          {unit && (
            <p
              className={cn(
                "text-body-s font-normal min-w-[60px] overflow-hidden text-ellipsis",
                unitColorClass,
              )}
            >
              {unit}
            </p>
          )}
          <p className={cn("text-body-s font-semibold min-w-[50px] overflow-hidden text-ellipsis", unitColorClass)}>
            {formatValue()}
          </p>
        </div>

        {/* Action Link */}
        {actionLabel && onAction && (
          <TeeLink
            variant="primary"
            size="s"
            leftIcon={leadingActionIcon}
            onClick={onAction}
            href="#"
            className="flex items-center gap-1.5 ml-6"
          >
            {actionLabel}
          </TeeLink>
        )}
      </div>
    );
  },
);

TeeDataDisplay.displayName = "TeeDataDisplay";

export { TeeDataDisplay as default };
